import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Headphones,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { SUPPORT_CHANNELS, SUPPORT_CONTACT, SUPPORT_FAQ } from '@/constants/supportContact';
import { cn } from '@/utils/cn';

const channelIcons = {
  phone: Phone,
  email: Mail,
  whatsapp: MessageCircle,
  location: MapPin,
} as const;

export function ContactSupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Headphones className="h-8 w-8" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-brand-navy">{SUPPORT_CONTACT.title}</h1>
        <p className="mt-1 text-sm font-semibold text-brand-green">{SUPPORT_CONTACT.subtitle}</p>
        <p className="mx-auto mt-2 max-w-xl text-sm text-brand-muted">{SUPPORT_CONTACT.description}</p>
      </div>

      <Card className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-brand-surface p-2.5 text-primary">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-brand-navy">Support hours</p>
            <p className="mt-1 text-sm text-brand-muted">{SUPPORT_CONTACT.hours}</p>
            <p className="mt-2 text-xs text-brand-muted">
              Outside these hours, send a WhatsApp message or email and we will get back to you.
            </p>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
          <h2 className="font-bold text-brand-navy">Get in touch</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {SUPPORT_CHANNELS.map((channel) => {
            const Icon = channelIcons[channel.id as keyof typeof channelIcons];
            const content = (
              <>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="rounded-xl bg-brand-surface p-2.5 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="text-xs font-medium text-brand-muted">{channel.label}</p>
                    <p className="truncate font-semibold text-brand-navy">{channel.value}</p>
                  </div>
                </div>
                {channel.href && <ChevronRight className="h-4 w-4 shrink-0 text-brand-muted" />}
              </>
            );

            if (channel.href) {
              return (
                <a
                  key={channel.id}
                  href={channel.href}
                  target={channel.external ? '_blank' : undefined}
                  rel={channel.external ? 'noreferrer' : undefined}
                  className="flex items-center justify-between gap-3 px-4 py-4 transition hover:bg-brand-surface/60 sm:px-5"
                >
                  {content}
                </a>
              );
            }

            return (
              <div key={channel.id} className="flex items-center justify-between gap-3 px-4 py-4 sm:px-5">
                {content}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
          <h2 className="font-bold text-brand-navy">Frequently asked questions</h2>
          <p className="mt-1 text-sm text-brand-muted">Find answers to common questions</p>
        </div>
        <div className="divide-y divide-gray-100">
          {SUPPORT_FAQ.map((item, index) => {
            const open = openFaq === index;
            return (
              <div key={item.question}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : index)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-5"
                >
                  <span className="font-medium text-brand-navy">{item.question}</span>
                  <ChevronDown className={cn('h-4 w-4 shrink-0 text-brand-muted transition', open && 'rotate-180')} />
                </button>
                {open && (
                  <div className="border-t border-gray-50 bg-brand-surface/40 px-4 py-3 text-sm leading-relaxed text-brand-muted sm:px-5">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
